import React from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';

// Components
import SearchableDropdown from '../../../../../components/inputs/SearchableDropdown';
import {
  RoadSelectField,
  RoadRangeRows,
} from '../../../../../components/forms/RoadRangesFields';
import InputText from '../../../../../components/inputs/InputText';
import { Typography } from '../../../../../components/display/Typography';
import MapWithSegments from '../MapWithSegments';
// D2 (2026-07-20): mismo textarea estilizado que usa el form canónico de OT
// (`NewWorkOrderForm/Description.tsx`) para el campo Descripción.
import { InputTextAreaStyled } from '../../../../../components/forms/components/MainStyled';

// Types
import { OptionSelected } from '../../../../../components/inputs/SearchableDropdown/types';

// Map base (mismo componente que el wizard de actuación)
import { MapComponent } from '../../../../../components/display/Map';
import { TenantMapCenter } from '../../../../../hooks/useTenantMapCenter';

// Utils
import { parsePk } from '../../../../../utils/transform';
import { registerPk } from '../../../../../utils/pkField';

// Schema
import {
  type NewRouteFormValues,
  type RouteRoadRangeFormValue,
} from '../../NewRoute/schema';

// Styles
import { ContentArea, MainContent } from '../MainStyledComponents';
import {
  FormSection,
  FormRow,
  FormField,
  PKRow,
  MapColumn,
  RoadChangeNotice,
  RoadChangeNoticeIcon,
} from './styles';

// ═══════════════════════════════════════════════════════════════════
// TAB UBICACIÓ — SearchableDropdown + InputPK + MapWithSegments
//
// Extraído de `NewRoute/index.tsx` (`renderUbicacio` + `renderMap`, Task 3
// del refactor de patrones RV, 2026-07-07) — misma lógica, sin cambios de
// comportamiento. La cascada de carretera (`client.query(GET_ROAD_BY_ID)`)
// se QUEDA en `NewRoute/index.tsx` (es orquestación de página) — este
// componente solo recibe los callbacks/valores ya resueltos.
// ═══════════════════════════════════════════════════════════════════

export interface UbicacioTabProps {
  register: UseFormRegister<NewRouteFormValues>;
  // Necesario para `registerPk`: normaliza el PK al salir del campo
  // (`90+50` → `90 + 050`).
  setValue: UseFormSetValue<NewRouteFormValues>;
  control: Control<NewRouteFormValues>;
  errors: FieldErrors<NewRouteFormValues>;
  // Tour P4 (2026-07-06) — regla canónica de LocationForm: si solo hay una
  // concesión (caso UTE) se auto-selecciona y el dropdown NO se muestra.
  singleConcession: boolean;
  concessionOptions: Array<{ label: string; value: string }>;
  roadOptions: Array<{ label: string; value: string }>;
  /** Plantillas de recorrido ACTIVAS que caben en la concesión elegida
   *  (2026-08-16). VACÍO = el tenant no usa plantillas y el campo «Nombre del
   *  recorrido» es el `InputText` de siempre: sin regresión ni migración. */
  presetOptions?: Array<{ label: string; value: string }>;
  handlePresetChange?: (presetId: string) => void;
  /** El usuario eligió «Otro nombre»: se muestra el campo de texto. */
  usingFreeName?: boolean;
  /** Lo que el desplegable debe estar enseñando: el id de la plantilla
   *  elegida, `__other__`, o vacío si aún no se ha elegido nada. */
  presetSelection?: string;
  handleConcessionChange: (opt: OptionSelected | undefined) => void;
  /** Multi-carretera (2026-08-15): un recorrido puede cubrir una troncal y su
   *  ramal, así que el selector es múltiple y cada carretera trae su fila de
   *  PK con sus propios extremos. */
  handleRoadsChange: (roadIds: string[]) => void | Promise<void>;
  roadDropdownRef: React.MutableRefObject<any>;
  roadIds: string[];
  roadRanges: RouteRoadRangeFormValue[];
  // Centro/zoom por defecto del mapa cuando aún no hay carretera elegida.
  defaultMap: TenantMapCenter;
  // Feature `surveillance_default_team` (2026-07-17): si el tenant NO tiene
  // equipo de vigilancia por defecto configurado, el wizard muestra este
  // selector (equipos isSurveillance). Con la config puesta (caso UTE →
  // ÈXSER) el selector NO se muestra y el equipo va preseleccionado.
  showTeamSelector?: boolean;
  teamOptions?: Array<{ label: string; value: string }>;
  selectedTeamId?: string;
  handleTeamChange?: (opt: OptionSelected | undefined) => void;
  /** Equipo OBLIGATORIO (2026-07-17): error inline bajo el selector cuando
   *  se intenta Iniciar sin equipo resuelto — mismo patrón borderError/error
   *  que concesión/carretera/PKs. */
  teamError?: string;
  /** Edición (F4 2026-07-17, spec §2.5): el RV tiene tramos registrados y se
   *  cambió la carretera. Cambiar la carretera NO modifica los tramos (su
   *  ubicación es registro histórico). MECÁNICA implementada aquí; el TEXTO
   *  del aviso (clave i18n nueva) se aprueba y renderiza en F5. */
  roadChangeWarning?: boolean;
}

export default function UbicacioTab({
  register,
  setValue,
  control,
  errors,
  singleConcession,
  concessionOptions,
  roadOptions,
  presetOptions,
  handlePresetChange,
  usingFreeName,
  presetSelection,
  handleConcessionChange,
  handleRoadsChange,
  roadDropdownRef,
  roadIds,
  roadRanges,
  defaultMap,
  showTeamSelector,
  teamOptions,
  selectedTeamId,
  handleTeamChange,
  teamError,
  roadChangeWarning,
}: UbicacioTabProps): JSX.Element {
  const { t } = useTranslation(['pages', 'common', 'buttons', 'forms']);

  // Mapa — dibuja TODAS las carreteras elegidas, cada una con el trozo que el
  // usuario acaba de acotar, y encuadra sobre el conjunto.
  //
  // Cada una lleva su propia ventana de PK porque sus kilometrajes no son
  // comparables: filtrar el ramal por el rango de la troncal devolvería un
  // trozo arbitrario. Las filas a medio rellenar se saltan en vez de tumbar el
  // mapa — mientras el usuario teclea un PK, el resto se sigue viendo.
  // 🔴 `useMemo` OBLIGATORIO, no es una optimización: este array es dependencia
  // del efecto que carga las mallas en `MapWithSegments`. Construido en cada
  // render, su identidad cambia siempre → el efecto se redispara sin parar y el
  // mapa se pasa la vida pidiendo geoposiciones, ahogando la página. Mismo
  // motivo que el `useMemo` de `useRoadPkRange`, y el mismo síntoma: nada falla
  // con un mensaje claro, simplemente todo va lento y las esperas expiran.
  const roadWindows = React.useMemo(
    () =>
      (roadRanges ?? []).flatMap((row) => {
        if (!row.roadId || !row.pkInit || !row.pkEnd) return [];
        const init = parsePk(row.pkInit);
        const end = parsePk(row.pkEnd);
        if (!init || !end) return [];
        return [
          {
            roadId: row.roadId,
            pkInitKm: init.km,
            pkInitMeter: init.meter,
            pkEndKm: end.km,
            pkEndMeter: end.meter,
          },
        ];
      }),
    // 🔴 La dependencia es una CLAVE SERIALIZADA, no el array. `watch()` de
    // react-hook-form devuelve una identidad nueva en cada render, así que
    // depender de `roadRanges` deja el memo inútil y el bucle intacto. Son
    // cuatro filas como mucho: serializar sale gratis y es lo único estable.
    [JSON.stringify(roadRanges ?? [])]
  );

  /** Ids de las carreteras con fila completa — misma estabilidad que arriba. */
  const roadIdsForMap = React.useMemo(
    () => roadWindows.map((w) => w.roadId),
    [roadWindows]
  );

  const renderMap = () => {
    if (roadWindows.length === 0) return null;
    const [primera] = roadWindows;
    return (
      <MapWithSegments
        segments={[]}
        hideLegend
        roadId={primera.roadId}
        roadIds={roadIdsForMap}
        roadWindows={roadWindows}
        pkInitKm={primera.pkInitKm}
        pkInitMeter={primera.pkInitMeter}
        pkEndKm={primera.pkEndKm}
        pkEndMeter={primera.pkEndMeter}
      />
    );
  };

  return (
    <ContentArea>
      <MainContent>
        <FormSection>
          <Typography component="h2" variant="semiBold" size="L">
            {t('pages:surveillance.location')}
          </Typography>

          {/* V4 FR-4.2: "Nom del recorregut" obligatorio.
              Plantillas (2026-08-16): con catálogo configurado el nombre se
              ELIGE —y de paso siembra carreteras y PK—, con una salida a texto
              libre para el recorrido no previsto. Sin plantillas, este bloque
              es exactamente el campo de texto de siempre. */}
          {(presetOptions?.length ?? 0) > 0 && (
            <FormRow>
              <FormField>
                <SearchableDropdown
                  labelText={t('pages:surveillance.name')}
                  placeholder={t('pages:surveillance.selectPreset')}
                  options={[
                    ...(presetOptions ?? []),
                    {
                      label: t('pages:surveillance.otherName'),
                      value: '__other__',
                    },
                  ]}
                  selectedOption={presetSelection ?? ''}
                  onChangeSelected={(opt) =>
                    handlePresetChange?.(opt?.value ?? '')
                  }
                  inputSize="M"
                  typeDropDown="Default"
                />
              </FormField>
            </FormRow>
          )}

          {((presetOptions?.length ?? 0) === 0 || usingFreeName) && (
            <FormRow>
              <FormField>
                <InputText
                  {...register('name')}
                  typeInput="normal"
                  label={t('pages:surveillance.name')}
                  placeholder={t('pages:surveillance.namePlaceholder')}
                  inputSize="L"
                  borderError={!!errors.name}
                  error={errors.name?.message}
                />
              </FormField>
            </FormRow>
          )}

          <FormRow>
            {/* Concesión oculta si solo hay una (auto-seleccionada arriba) —
                regla canónica de LocationForm. */}
            {!singleConcession && (
              <FormField>
                <Controller
                  control={control}
                  name="concessionId"
                  render={({ field: { value } }) => (
                    <SearchableDropdown
                      labelText={t('pages:surveillance.concession')}
                      placeholder={t('pages:surveillance.selectConcession')}
                      options={concessionOptions}
                      selectedOption={value}
                      onChangeSelected={handleConcessionChange}
                      inputSize="M"
                      typeDropDown="Default"
                      borderError={!!errors.concessionId}
                      error={errors.concessionId?.message}
                    />
                  )}
                />
              </FormField>
            )}
            <FormField>
              <Controller
                control={control}
                name="roadIds"
                render={({ field: { value } }) => (
                  <RoadSelectField
                    dropdownRef={roadDropdownRef}
                    roadOptions={roadOptions}
                    roadIds={value ?? []}
                    onRoadsChange={handleRoadsChange}
                    roadsError={
                      // yup pone el mensaje en el array cuando está vacío.
                      (errors.roadIds as { message?: string } | undefined)
                        ?.message
                    }
                  />
                )}
              />
            </FormField>
            {/* Sentido eliminado del wizard — ahora se pregunta en el
                StartTimerModal al iniciar cada segment del timer
                (ArrowDirectionButton + Carril dropdown) */}
          </FormRow>

          {/* Aviso §2.5 (edición con tramos + carretera cambiada). MECÁNICA F4:
              `roadChangeWarning` ya llega calculado (RV con tramos + carretera
              cambiada respecto al valor original). TEXTO cableado en F5 tras
              el gate i18n del usuario — clave `pages:surveillance.
              roadChangeSegmentsWarning` (ca/es/en). */}
          {roadChangeWarning && (
            <RoadChangeNotice role="alert">
              <RoadChangeNoticeIcon aria-hidden="true">⚠</RoadChangeNoticeIcon>
              <span>{t('pages:surveillance.roadChangeSegmentsWarning')}</span>
            </RoadChangeNotice>
          )}

          {/* Equipo del recorrido — SOLO si hay algo que elegir. Si el equipo
              se resuelve solo (config `surveillance_default_team` del tenant, o
              un único equipo de vigilancia) este selector no aparece, ni al
              crear ni al editar: únicamente podría volver a poner lo que ya
              está puesto. Lo decide `showTeamSelector` en el asistente. */}
          {showTeamSelector && (
            <FormRow>
              <FormField>
                <SearchableDropdown
                  id="route-team-select"
                  labelText={t('forms:workOrder.team')}
                  placeholder={t('common:placeholders.select')}
                  options={teamOptions ?? []}
                  selectedOption={selectedTeamId ?? ''}
                  onChangeSelected={handleTeamChange ?? (() => undefined)}
                  inputSize="M"
                  typeDropDown="Default"
                  borderError={!!teamError}
                  error={teamError}
                />
              </FormField>
            </FormRow>
          )}

          {/* Una fila por carretera: sus extremos (read-only) y sus PK
              editables. El bloque vive en `RoadRangesFields` porque lo comparte
              el modal de Gestión que define las plantillas de recorrido. */}
          <RoadRangeRows
            roadOptions={roadOptions}
            roadRanges={roadRanges ?? []}
            registerPkField={(index, field) =>
              registerPk(register, setValue, `roadRanges.${index}.${field}`)
            }
            errorFor={(index, field) =>
              (
                errors.roadRanges as
                  | Array<{
                      pkInit?: { message?: string };
                      pkEnd?: { message?: string };
                    }>
                  | undefined
              )?.[index]?.[field]?.message
            }
          />

          {/* D2 (2026-07-20): "Descripción" OPCIONAL — SIEMPRE el último campo
              del tab (decisión usuario 2026-07-20). Guarda en `Order.description`
              de la gemela (entidad doble), separada de `name` (Addendum (2)).
              Reusa componente + clave del resto de la app
              (`forms:workOrder.description`). SIN campo Notas. */}
          <FormRow>
            <InputTextAreaStyled
              {...register('description')}
              id="description-input"
              name="description"
              disabled={false}
              label={t('forms:workOrder.description')}
              placeholder={t('forms:placeholders.description')}
              borderError={!!errors.description}
              error={errors.description?.message}
            />
          </FormRow>
        </FormSection>
      </MainContent>

      {/* Mockup del spec (fix 2026-07-04): mapa a la DERECHA del form. Se
          pinta en cuanto hay carretera (los PKs se pre-rellenan con los
          límites de la vía al seleccionarla). */}
      <MapColumn>
        {/* Feedback usuario 2026-07-04: sin carretera se muestra el mapa
            base centrado en la zona del tenant (config `map_default_center`,
            fallback Cataluña, mismo MapComponent que el wizard de actuación);
            al elegir vía se pinta el trazado con los PKs. */}
        {renderMap() ?? (
          <MapComponent
            center={defaultMap.center}
            zoom={defaultMap.zoom}
            path={[]}
            geoloc={null}
            apiKey={import.meta.env.VITE_MAP_API_KEY}
          />
        )}
      </MapColumn>
    </ContentArea>
  );
}
